import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { SUPPRESSION_MAX_DAYS, SUPPRESSION_MIN_DAYS } from '../../models/disposition';

// What the reader chose. Absent is a third answer and never means "no suppression":
// escape and the backdrop both close a dialog, and a reader who backed out of the question
// did not ask for the Issue to be closed either.
export interface SuppressDialogResult {
  suppress: boolean;
  // Zero whenever suppress is false, so the caller never reads a window off a declined
  // answer.
  days: number;
}

export interface SuppressDialogData {
  // The window the box opens on.
  days: number;
}

// Asked once, for the two verdicts that can buy quiet: false positive and no action needed.
@Component({
  selector: 'app-suppress-dialog',
  templateUrl: './suppress-dialog.component.html',
  styleUrls: ['./suppress-dialog.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SuppressDialogComponent {
  readonly minDays = SUPPRESSION_MIN_DAYS;
  readonly maxDays = SUPPRESSION_MAX_DAYS;

  // Bound to the box, so it holds whatever was typed, including nothing. The guard below
  // is what decides whether that is a number anyone may act on.
  days: number | null;

  constructor(
    private readonly dialogRef: MatDialogRef<SuppressDialogComponent, SuppressDialogResult>,
    @Inject(MAT_DIALOG_DATA) data: SuppressDialogData,
  ) {
    this.days = data?.days ?? SUPPRESSION_MIN_DAYS;
  }

  // A number input hands back null for an empty box and for text it cannot parse, and it
  // does not enforce min and max on typing. So every one of those is checked here rather
  // than trusted to the element.
  get valid(): boolean {
    return Number.isInteger(this.days)
      && (this.days as number) >= this.minDays
      && (this.days as number) <= this.maxDays;
  }

  suppress(): void {
    if (!this.valid) return;
    this.dialogRef.close({ suppress: true, days: this.days as number });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  decline(): void {
    this.dialogRef.close({ suppress: false, days: 0 });
  }
}
